import React from 'react';
import { useField } from 'formik';

import Grid from '@mui/material/Grid';
import type { SwitchProps as MuiSwitchProps } from '@mui/material/Switch';
import MuiSwitch from '@mui/material/Switch';

import Typography from 'src/components/ui/Typography';

import { colours } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';

const StyledSwitch = styledComponent(MuiSwitch)`
    .MuiSwitch-colorPrimary {
        color: ${colours.primary.O87};
        ~ .MuiSwitch-track {
            background-color: ${colours.primary.main};
        }
        &.Mui-checked {
            color: ${colours.secondary.main};
            ~ .MuiSwitch-track {
                background-color: ${colours.secondary.main};
                opacity: 0.6;
            }
        }
    }
    .MuiSwitch-colorSecondary {
        color: ${colours.secondary.O87};
        ~ .MuiSwitch-track {
            background-color: ${colours.secondary.main};
        }
        &.Mui-checked {
            color: ${colours.secondary.main};
            ~ .MuiSwitch-track {
                opacity: 0.6;
            }
        }
    }
    .Mui-disabled {
    color: ${colours.grey.G100} !important;
    ~ .MuiSwitch-track {
        background-color: ${colours.primary.main} !important;
        opacity: 0.12 !important;
    }
}
`;

const StyledTypography = styledComponent(Typography)`
    cursor: pointer;
`;

StyledSwitch.defaultProps = {
    color: 'primary',
};

type SwitchProps = Omit<MuiSwitchProps, 'onChange' | 'value'> & {
    onLabel?: string;
    offLabel?: string;
    value?: boolean;
    onChange: (value: boolean) => void;
    container?: boolean;
};

export const Switch: React.FC<SwitchProps> = ({ onLabel, offLabel, value = false, onChange, container, ...rest }) => (
    <Grid component="label" alignItems="center" spacing={1} container={container}>
        {offLabel && (
            <Grid item>
                <StyledTypography variant="subtitle2" color={value ? undefined : 'primary'}>
                    {offLabel}
                </StyledTypography>
            </Grid>
        )}
        <Grid item>
            <StyledSwitch checked={value} onChange={(_event, newValue) => onChange(newValue)} {...rest} />
        </Grid>
        {onLabel && (
            <Grid item>
                <StyledTypography variant="subtitle2" color={value ? 'primary' : undefined}>
                    {onLabel}
                </StyledTypography>
            </Grid>
        )}
    </Grid>
);

type FormSwitchProps = Omit<SwitchProps, 'onChange'> & {
    name: string;
};

/**
 * Swith component used in forms. It uses Formik to manage the state.
 */
export const FormSwitch: React.FC<FormSwitchProps> = ({ name, value, container = true, ...rest }) => {
    const [field, meta, helpers] = useField<boolean>({
        name,
    });

    const { setValue } = helpers;

    const checked = value ?? field.value;

    return <Switch name={name} onChange={setValue} value={checked} container={container} {...rest} />;
};
